import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Inbox, Mail, Phone, Download } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { listSubmissions, submissionStats, updateSubmission, formatApiError } from "@/lib/cms";

const STATUS = ["new", "in-progress", "resolved"];
const STATUS_STYLE = { new: "bg-primary text-white", "in-progress": "bg-secondary text-black", resolved: "bg-foreground text-background" };

export const SubmissionsView = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [subs, st] = await Promise.all([listSubmissions(), submissionStats()]);
      setRows(subs);
      setStats(st);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await updateSubmission(id, status);
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = () => {
    if (!rows.length) {
      toast.error("No submissions to export");
      return;
    }
    const cols = ["name", "email", "phone", "company", "category", "subject", "message", "status", "created_at"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    rows.forEach((r) => lines.push(cols.map((c) => escape(r[c])).join(",")));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rtpl-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="submissions-view">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">Support Submissions</h2>
        <button
          onClick={exportCsv}
          data-testid="export-csv-btn"
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-primary/90"
        >
          <Download className="h-4 w-4" /> Download CSV
        </button>
      </div>
      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
          <div className="bg-card p-6"><div className="font-heading text-3xl font-bold text-primary">{stats.total}</div><div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Total</div></div>
          <div className="bg-card p-6"><div className="font-heading text-3xl font-bold text-foreground">{stats.new}</div><div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">New</div></div>
          <div className="bg-card p-6"><div className="font-heading text-3xl font-bold text-foreground">{stats.by_category?.["Dealership Request"] || 0}</div><div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Dealership</div></div>
          <div className="bg-card p-6"><div className="font-heading text-3xl font-bold text-foreground">{stats.by_category?.["Interested to Purchase"] || 0}</div><div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Purchase</div></div>
        </div>
      )}
      <div className="border border-border bg-card">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-muted-foreground"><Inbox className="h-12 w-12" /><p className="mt-4 text-sm">No submissions yet.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="submissions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead><TableHead>Category</TableHead><TableHead>Subject</TableHead>
                  <TableHead>Message</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`submission-row-${r.id}`}>
                    <TableCell className="align-top">
                      <div className="font-bold text-foreground">{r.name}</div>
                      {r.company && <div className="text-xs text-muted-foreground">{r.company}</div>}
                      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> {r.email}</div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" /> {r.phone}</div>
                    </TableCell>
                    <TableCell className="align-top"><Badge variant="outline" className="rounded-md border-primary text-primary">{r.category}</Badge></TableCell>
                    <TableCell className="align-top font-medium text-foreground">{r.subject}</TableCell>
                    <TableCell className="max-w-xs align-top text-sm text-muted-foreground">{r.message}</TableCell>
                    <TableCell className="align-top text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="align-top">
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                        <SelectTrigger className="w-36 rounded-lg border-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS.map((s) => (
                            <SelectItem key={s} value={s}>
                              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[s]}`}>{s}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};
